import React, { useMemo } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation, Trans } from 'react-i18next';
import { Theme } from '@app/theme';
import { InformationBox } from 'components';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  section: {
    paddingTop: theme.marginBase * 8,
    maxHeight: 'none',
    transition: [
      ['max-height', '.3s'],
      ['padding', '.3s'],
      ['opacity', '.1s'],
    ],
    overflow: 'visible',
    '&:last-child': {
      paddingBottom: theme.marginBase * 8,
    },
  },
  hidden: {
    maxHeight: 0,
    padding: 0,
    opacity: 0,
    overflow: 'hidden',
  },
  sectionTitle: {
    ...theme.fonts.h3,
  },
  explanation: {
    ...theme.fonts.body,
    marginTop: theme.marginBase * 3,
    color: theme.colors.inkSecondary,
  },
  box: {
    maxWidth: theme.maxContentWidth.screen,
    width: '100%',
    margin: 'auto',
    marginBottom: theme.marginBase * 4,
  },
}));

interface Props {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  id?: string;
  className?: string;
  withWarningBox?: boolean;
  hasParticipant?: boolean;
  hidden?: boolean;
  maxHeight?: number;
}

export const SessionFormSection = ({
  title,
  description,
  children,
  className,
  withWarningBox,
  hasParticipant,
  hidden,
  maxHeight,
  ...rest
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const style = useMemo(
    () => ({ maxHeight: hidden ? 0 : maxHeight }),
    [maxHeight, hidden],
  );

  return (
    <section
      className={classnames(classes.section, className, {
        [classes.hidden]: !!hidden,
      })}
      style={style}
      {...rest}
    >
      {withWarningBox && (
        <InformationBox
          text={
            hasParticipant
              ? t('editSession.bookedMessage')
              : t('editSession.publishedMessage')
          }
          className={classes.box}
        />
      )}
      {title && <h2 className={classes.sectionTitle}>{title}</h2>}
      {description && <p className={classes.explanation}>{description}</p>}
      {children}
    </section>
  );
};
